import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import axios from 'axios';
import {
  Wrench, AlertTriangle, Clock, Plus, Users, Timer, CheckCircle2, UserPlus,
  Search, X, Truck, User, Container, Gauge, ShieldAlert, ArrowRight, ChevronRight,
  Activity, CircleDot, ClipboardCheck, BarChart3, DollarSign, Download, Eye, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import AtelierInvitationBanner from '../components/AtelierInvitationBanner';
import { MecanicienTabs } from '../components/MecanicienTabs';
import FlaggedInspectionCard from '../../components/FlaggedInspectionCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = { open: 'Ouvert', in_progress: 'En cours', completed: 'Complete' };
const STATUS_COLORS = {
  open: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const HEALTH_COLORS = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-500' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-500' },
  good: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  none: { bg: 'bg-slate-800/50', border: 'border-slate-700/50', text: 'text-slate-500', dot: 'bg-slate-600' },
};

const HEALTH_LABELS = { critical: 'Critique', warning: 'Attention', good: 'OK', none: 'Aucune mesure' };

const TABS = [
  { key: '', label: 'Tous' },
  { key: 'open', label: 'Ouverts' },
  { key: 'in_progress', label: 'En cours' },
  { key: 'completed', label: 'Completes' },
  { key: 'unassigned', label: 'Non assignes' },
];

const TASK_LABELS = {
  diagnostic: 'Diag', reparation: 'Rep', entretien: 'Entr',
  inspection: 'Insp', mesures_pneus: 'Pneus', mesures_freins: 'Freins',
};

export default function AdminAtelierPage() {
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [flaggedInspections, setFlaggedInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [fleetFilter, setFleetFilter] = useState('all');
  const [correctionModal, setCorrectionModal] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, ordersRes, mechRes, vehRes, dashRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanic/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/mechanic/admin/work-orders`, { headers }),
        axios.get(`${API_URL}/api/mechanic/admin/users`, { headers }),
        axios.get(`${API_URL}/api/vehicles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/mechanic/dashboard`, { headers }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setMechanics(mechRes.data);
      setVehicles(vehRes.data || []);
      setFlaggedInspections(dashRes.data?.flagged_inspections || []);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAssign = async (orderId, mechanicId) => {
    try {
      await axios.patch(`${API_URL}/api/mechanic/admin/work-orders/${orderId}/assign?mechanic_id=${mechanicId}`, {}, { headers });
      toast.success('Bon assigne');
      setAssignModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleReopen = async (orderId) => {
    if (!window.confirm('Rouvrir ce bon pour correction?')) return;
    try {
      await axios.patch(`${API_URL}/api/mechanic/admin/work-orders/${orderId}/reopen`, {}, { headers });
      toast.success('Bon rouvert pour correction');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleCorrection = async (vehicleId, correctionType, notes) => {
    try {
      await axios.post(`${API_URL}/api/mechanic/admin/vehicles/${vehicleId}/correction`, {
        vehicle_id: vehicleId, correction_type: correctionType, notes
      }, { headers });
      toast.success('Correction enregistree - notifications resolues');
      setCorrectionModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const filtered = orders.filter(o => {
    if (activeTab === 'unassigned') return !o.mechanic_id;
    if (activeTab && o.status !== activeTab) return false;
    if (search) {
      const s = search.toLowerCase();
      return [o.order_number, o.vehicle_plate, o.vehicle_unit, o.client_name, o.mechanic_name]
        .filter(Boolean).join(' ').toLowerCase().includes(s);
    }
    return true;
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
    </div>
  );

  const totals = stats?.totals || {};
  const inspections = stats?.inspections || {};
  const mechStats = stats?.mechanics || [];
  const fleet = stats?.fleet || {};
  const fleetHealth = fleet.health || [];
  const recentMeasurements = stats?.recent_measurements || [];

  const filteredFleet = fleetHealth.filter(v => {
    if (fleetFilter === 'critical') return v.status === 'critical';
    if (fleetFilter === 'warning') return v.status === 'warning';
    if (fleetFilter === 'good') return v.status === 'good';
    if (fleetFilter === 'none') return v.status === 'none';
    if (fleetFilter === 'admin') return v.owner_type !== 'atelier';
    if (fleetFilter === 'atelier') return v.owner_type === 'atelier';
    return true;
  });

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="admin-atelier-page">
      <MecanicienTabs />
      {/* Atelier Invitation Banner */}
      <AtelierInvitationBanner />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Atelier</h1>
          <p className="text-sm text-slate-400">Bons de travail, flotte et mesures</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 text-sm"
          data-testid="admin-create-order-btn">
          <Plus size={16} /> Nouveau bon
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard icon={Wrench} color="text-blue-400" value={totals.open || 0} label="Bons ouverts" />
        <StatCard icon={CheckCircle2} color="text-emerald-400" value={totals.completed_today || 0} label="Completes auj." />
        <StatCard icon={UserPlus} color="text-purple-400" value={totals.unassigned || 0} label="Non assignes" />
        <StatCard icon={Truck} color="text-cyan-400" value={fleet.total || 0} label="Vehicules" />
        <StatCard icon={ShieldAlert} color="text-red-400" value={fleet.critical || 0} label="Critiques" alert={fleet.critical > 0} />
        <StatCard icon={AlertTriangle} color="text-amber-400" value={fleet.warning || 0} label="Attention" />
        <StatCard icon={Activity} color="text-emerald-400" value={recentMeasurements.length} label="Mesures recentes" />
      </div>

      {/* Atelier Fleet Button - prominent position */}
      <div
        onClick={() => navigate('/admin/fleet-atelier')}
        className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 cursor-pointer hover:bg-purple-500/15 transition-colors flex items-center justify-between"
        data-testid="atelier-fleet-btn"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
            <Wrench size={20} className="text-purple-400" />
          </div>
          <div>
            <p className="text-white font-medium">Flotte Atelier</p>
            <p className="text-xs text-purple-300/60">Gerez les vehicules de vos clients atelier</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-purple-400" />
      </div>

      {/* Atelier Invoices Section */}
      <AtelierInvoicesSection token={token} />

      {/* Inspections avec defauts - comme dans l'interface mecanicien */}
      {flaggedInspections.length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" />
            Inspections avec defauts
            <span className="text-xs text-slate-500 font-normal">({flaggedInspections.length})</span>
          </h2>
          <div className="space-y-2">
            {flaggedInspections.map((insp, i) => (
              <FlaggedInspectionCard
                key={insp.id || i}
                inspection={insp}
                testIdPrefix="admin-atelier-flagged"
                onCreateWorkOrder={(ins) => navigate(`/admin/mechanic/work-orders/new?from_inspection=${ins.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Fleet Health Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <Gauge size={18} className="text-amber-500" /> Sante de la flotte
            </h2>
            <div className="flex items-center gap-1 text-xs">
              <span className="flex items-center gap-1 text-slate-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Admin: {fleet.admin_vehicles || 0}
              </span>
              <span className="mx-1 text-slate-700">|</span>
              <span className="flex items-center gap-1 text-slate-500">
                <span className="w-2 h-2 rounded-full bg-amber-500" /> Clients: {fleet.atelier_vehicles || 0}
              </span>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[
              { key: 'all', label: 'Tous' },
              { key: 'critical', label: 'Critiques', color: 'text-red-400' },
              { key: 'warning', label: 'Attention', color: 'text-amber-400' },
              { key: 'good', label: 'OK', color: 'text-emerald-400' },
              { key: 'none', label: 'Sans mesure' },
              { key: 'admin', label: 'Admin' },
              { key: 'atelier', label: 'Clients' },
            ].map(f => (
              <button key={f.key} onClick={() => setFleetFilter(f.key)}
                data-testid={`fleet-filter-${f.key}`}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  fleetFilter === f.key
                    ? 'bg-amber-500 text-black'
                    : `bg-slate-800 ${f.color || 'text-slate-400'} hover:bg-slate-700`
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 max-h-[600px] overflow-y-auto">
          {filteredFleet.length === 0 ? (
            <p className="text-slate-500 text-sm col-span-3 text-center py-6">Aucun vehicule dans ce filtre</p>
          ) : (
            filteredFleet.map(v => {
              const h = HEALTH_COLORS[v.status] || HEALTH_COLORS.none;
              const isAtelier = v.owner_type === 'atelier';
              const unitLabel = (u) => ({ mm: 'mm', pouces: 'po', '32emes': '/32', pourcent: '%' }[u] || u);
              const statusLabels = { good: 'OK', warning: 'Attention', critical: 'Critique', none: '-' };
              const dotColors = { good: 'bg-green-500', warning: 'bg-amber-500', critical: 'bg-red-500', none: 'bg-slate-600' };
              const statusTextColor = { good: 'text-green-400', warning: 'text-amber-400', critical: 'text-red-400', none: 'text-slate-500' };
              return (
                <div key={v.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid={`fleet-health-${v.id}`}>
                  <div className="p-3">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {v.vehicle_type === 'trailer'
                          ? <Container size={16} className="text-blue-400" />
                          : <Truck size={16} className={h.text} />}
                        <div>
                          <p className="text-white text-sm font-bold">
                            #{v.unit_number || '?'} <span className="font-normal text-slate-400">{v.plate_number}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isAtelier ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                          {isAtelier ? 'Client' : 'Admin'}
                        </span>
                        <span className={`w-2.5 h-2.5 rounded-full ${h.dot}`} />
                      </div>
                    </div>

                    {/* Vehicle details grid */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] mb-2">
                      {v.brand && <p className="text-slate-400"><span className="text-slate-600">Marque: </span>{v.brand}</p>}
                      {v.model && <p className="text-slate-400"><span className="text-slate-600">Modele: </span>{v.model}</p>}
                      {v.year && <p className="text-slate-400"><span className="text-slate-600">Annee: </span>{v.year}</p>}
                      {v.current_odometer > 0 && <p className="text-slate-400"><span className="text-slate-600">Odo: </span>{v.current_odometer?.toLocaleString()} km</p>}
                      {v.pnbv && <p className="text-slate-400"><span className="text-slate-600">PNBV: </span>{v.pnbv?.toLocaleString()} kg</p>}
                      {v.vin && <p className="text-slate-400 col-span-2"><span className="text-slate-600">VIN: </span><span className="font-mono text-[10px]">{v.vin}</span></p>}
                    </div>

                    {/* Measurement badges */}
                    {v.status !== 'none' ? (
                      <div className={`flex items-center gap-3 px-2.5 py-1.5 rounded-lg mb-2 ${
                        v.status === 'critical' ? 'bg-red-500/10 border border-red-500/20' :
                        v.status === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
                        'bg-green-500/10 border border-green-500/20'
                      }`}>
                        <Gauge size={12} className={statusTextColor[v.status]} />
                        <div className="flex items-center gap-3 text-xs flex-1">
                          <span className="flex items-center gap-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${dotColors[v.brake_status]}`} />
                            <span className="text-slate-500">Freins:</span>
                            <span className={`font-semibold ${statusTextColor[v.brake_status]}`}>
                              {statusLabels[v.brake_status]}
                              {v.min_brake != null && <span className="text-slate-500 font-normal ml-0.5">({v.min_brake}{unitLabel(v.brake_unit)})</span>}
                            </span>
                          </span>
                          <span className="text-slate-700">|</span>
                          <span className="flex items-center gap-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${dotColors[v.tire_status]}`} />
                            <span className="text-slate-500">Pneus:</span>
                            <span className={`font-semibold ${statusTextColor[v.tire_status]}`}>
                              {statusLabels[v.tire_status]}
                              {v.min_tire != null && <span className="text-slate-500 font-normal ml-0.5">({v.min_tire}{unitLabel(v.tire_unit)})</span>}
                            </span>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg mb-2 bg-slate-800/50 border border-slate-700/30">
                        <Gauge size={12} className="text-slate-600" />
                        <span className="text-[11px] text-slate-600">Aucune mesure enregistree</span>
                      </div>
                    )}

                    {v.last_measurement_date && (
                      <p className="text-[10px] text-slate-500 mb-2">
                        Derniere mesure: {v.last_measurement_date} {v.last_measurement_by ? `par ${v.last_measurement_by}` : ''}
                      </p>
                    )}

                    {/* Action buttons - same as Admin Generale */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <button onClick={() => navigate(`/admin/fleet/${v.id}/inspection`)}
                        className="flex items-center justify-center gap-1 py-1.5 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 text-[11px] font-medium transition-colors"
                        data-testid={`atelier-inspect-${v.id}`}>
                        <ClipboardCheck size={12} /> Inspection
                      </button>
                      <button onClick={() => navigate(`/admin/fleet/${v.id}/measurements`)}
                        className="flex items-center justify-center gap-1 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 text-[11px] font-medium transition-colors"
                        data-testid={`atelier-measure-${v.id}`}>
                        <Gauge size={12} /> Mesures
                      </button>
                      <button onClick={() => setCorrectionModal(v)}
                        className="flex items-center justify-center gap-1 py-1.5 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white text-[11px] font-medium transition-colors"
                        data-testid={`atelier-stats-${v.id}`}>
                        <BarChart3 size={12} /> Stats
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Recent Measurements Timeline */}
      {recentMeasurements.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Activity size={18} className="text-amber-500" /> Dernieres mesures
          </h2>
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {recentMeasurements.map(m => (
              <div key={m.id} className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-2.5" data-testid={`recent-measurement-${m.id}`}>
                <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Gauge size={14} className="text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {m.vehicle_unit ? `#${m.vehicle_unit}` : 'Vehicule'} - {m.measurement_date}
                  </p>
                  <p className="text-xs text-slate-400">
                    {m.created_by_name || 'Mecanicien'}
                    {m.source === 'atelier' && <span className="text-amber-400/70 ml-1">(Atelier)</span>}
                    {m.odometer > 0 && <span className="ml-2">{m.odometer.toLocaleString()} km</span>}
                  </p>
                </div>
                <span className="text-[10px] text-slate-500 flex-shrink-0">{m.created_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Productivity per Mechanic */}
      {mechStats.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Timer size={18} className="text-amber-500" /> Productivite par mecanicien
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left text-slate-400">
                  <th className="pb-2 font-medium">Mecanicien</th>
                  <th className="pb-2 font-medium text-center">Ouverts</th>
                  <th className="pb-2 font-medium text-center">Completes</th>
                  <th className="pb-2 font-medium text-center">Total</th>
                  <th className="pb-2 font-medium text-right">Temps</th>
                </tr>
              </thead>
              <tbody>
                {mechStats.map(m => (
                  <tr key={m.id} className="border-b border-slate-800 last:border-0">
                    <td className="py-2.5 font-medium text-slate-200">{m.name}</td>
                    <td className="py-2.5 text-center"><span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded text-xs">{m.open_orders}</span></td>
                    <td className="py-2.5 text-center"><span className="bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded text-xs">{m.completed_orders}</span></td>
                    <td className="py-2.5 text-center text-slate-300">{m.total_orders}</td>
                    <td className="py-2.5 text-right font-mono text-amber-400">{m.total_time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Work Orders */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-semibold text-white">Bons de travail</h2>
            <span className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full">{filtered.length}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === tab.key ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                }`}
                data-testid={`admin-tab-${tab.key || 'all'}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative mt-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..." className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              data-testid="admin-search" />
          </div>
        </div>

        <div className="divide-y divide-slate-800 max-h-[500px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Aucun bon de travail</div>
          ) : (
            filtered.map(order => (
              <div key={order.id} className="p-4 hover:bg-slate-800/50 transition-colors" data-testid={`admin-order-${order.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-white">{order.order_number}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_COLORS[order.status] || 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                        {STATUS_LABELS[order.status] || order.status}
                      </span>
                      {!order.mechanic_id && (
                        <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full border border-purple-500/20">Non assigne</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-400">
                      {order.mechanic_name && <span className="flex items-center gap-1"><Users size={10} /> {order.mechanic_name}</span>}
                      {order.vehicle_plate && <span className="flex items-center gap-1"><Truck size={10} /> {order.vehicle_plate}</span>}
                      {order.client_name && <span className="flex items-center gap-1"><User size={10} /> {order.client_name}</span>}
                      <span>{order.date}</span>
                    </div>
                    {(order.tasks || []).length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {order.tasks.map((t, i) => (
                          <span key={i} className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">
                            {TASK_LABELS[t.type] || t.type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setAssignModal(order)}
                    className="p-2 rounded-lg bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-colors"
                    title="Assigner" data-testid={`assign-btn-${order.id}`}>
                    <UserPlus size={16} />
                  </button>
                  {order.status === 'completed' && (
                    <button onClick={() => handleReopen(order.id)}
                      className="p-2 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
                      title="Rouvrir pour correction" data-testid={`reopen-btn-${order.id}`}>
                      <Wrench size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()} data-testid="assign-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-white">Assigner {assignModal.order_number}</h3>
              <button onClick={() => setAssignModal(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            {assignModal.mechanic_name && (
              <p className="text-sm text-slate-400 mb-3">Actuellement: <strong className="text-slate-200">{assignModal.mechanic_name}</strong></p>
            )}
            <div className="space-y-2">
              {mechanics.map(m => (
                <button key={m.id} onClick={() => handleAssign(assignModal.id, m.id)}
                  className={`w-full p-3 rounded-xl text-left border transition-colors ${
                    assignModal.mechanic_id === m.id ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 hover:bg-slate-800'
                  }`}
                  data-testid={`assign-to-${m.id}`}>
                  <p className="font-medium text-slate-200">{m.first_name} {m.last_name}</p>
                  <p className="text-xs text-slate-400">{m.email}</p>
                </button>
              ))}
              {mechanics.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Aucun mecanicien. Ajoutez-en dans Parametres.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Work Order Modal */}
      {showCreateModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); fetchAll(); }}
          mechanics={mechanics}
          vehicles={vehicles}
          token={token}
        />
      )}

      {/* Correction Modal */}
      {correctionModal && (
        <CorrectionModal
          vehicle={correctionModal}
          onClose={() => setCorrectionModal(null)}
          onCorrect={handleCorrection}
        />
      )}
    </div>
  );
}


function StatCard({ icon: Icon, color, value, label, alert }) {
  return (
    <div className={`bg-slate-900 border rounded-xl p-4 ${alert ? 'border-red-500/40 animate-pulse' : 'border-slate-800'}`}>
      <Icon size={18} className={`${color} mb-1`} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}


function CreateWorkOrderModal({ onClose, onCreated, mechanics, vehicles, token }) {
  const [form, setForm] = useState({
    mechanic_id: '', vehicle_type: 'fleet', vehicle_plate: '', vehicle_unit: '',
    vehicle_brand: '', vehicle_model: '', vehicle_odometer: '',
    client_name: '', client_phone: '', notes: '',
    tasks: [],
  });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const selectVehicle = (vid) => {
    const v = vehicles.find(x => x.id === vid);
    if (v) setForm(prev => ({
      ...prev, vehicle_id: v.id,
      vehicle_plate: v.plate_number || '', vehicle_unit: v.unit_number || '',
      vehicle_brand: v.brand || v.make || '', vehicle_model: v.model || '',
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/mechanic/admin/work-orders`, {
        ...form,
        mechanic_id: form.mechanic_id || null,
      }, { headers });
      toast.success(`Bon ${res.data.order_number} cree`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-xl" onClick={e => e.stopPropagation()} data-testid="create-order-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-white">Nouveau bon de travail</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Assigner a</label>
            <select value={form.mechanic_id} onChange={e => setForm(prev => ({ ...prev, mechanic_id: e.target.value }))} className={inputCls} data-testid="create-assign-select">
              <option value="">-- Non assigne (premier disponible) --</option>
              {mechanics.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Vehicule</label>
            {vehicles.length > 0 && (
              <select onChange={e => selectVehicle(e.target.value)} value="" className={`${inputCls} mb-2`}>
                <option value="">-- Choisir de la flotte --</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.unit_number ? `#${v.unit_number} ` : ''}{v.plate_number} {v.brand || v.make} {v.model}</option>)}
              </select>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={form.vehicle_plate} onChange={e => setForm(prev => ({ ...prev, vehicle_plate: e.target.value }))} placeholder="Plaque" className={inputCls} />
              <input type="text" value={form.vehicle_unit} onChange={e => setForm(prev => ({ ...prev, vehicle_unit: e.target.value }))} placeholder="No unite" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Client</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={form.client_name} onChange={e => setForm(prev => ({ ...prev, client_name: e.target.value }))} placeholder="Nom" className={inputCls} />
              <input type="text" value={form.client_phone} onChange={e => setForm(prev => ({ ...prev, client_phone: e.target.value }))} placeholder="Telephone" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Notes / Instructions</label>
            <textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} placeholder="Instructions pour le mecanicien..." className={inputCls} />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-700 rounded-lg text-sm text-slate-400 hover:bg-slate-800 transition-colors">Annuler</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg text-sm disabled:opacity-50"
            data-testid="create-order-submit">
            {saving ? 'Creation...' : 'Creer le bon'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CorrectionModal({ vehicle, onClose, onCorrect }) {
  const [correctionType, setCorrectionType] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500";

  const vehLabel = `${vehicle.unit_number ? `#${vehicle.unit_number} ` : ''}${vehicle.plate_number || ''}`.trim();

  const handleSubmit = async () => {
    if (!correctionType) return;
    setSaving(true);
    await onCorrect(vehicle.id, correctionType, notes);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()} data-testid="correction-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-white">Corriger - {vehLabel}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        <p className="text-sm text-slate-400 mb-4">
          Enregistrer la correction effectuee. Les notifications liees seront automatiquement marquees comme resolues.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Type de correction</label>
            <select value={correctionType} onChange={e => setCorrectionType(e.target.value)} className={inputCls} data-testid="correction-type-select">
              <option value="">-- Choisir --</option>
              <option value="brakes_replaced">Freins remplaces</option>
              <option value="brakes_adjusted">Freins ajustes</option>
              <option value="tires_replaced">Pneus remplaces</option>
              <option value="tires_rotated">Pneus permutes</option>
              <option value="inspection_corrected">Defauts inspection corriges</option>
              <option value="external_repair">Reparation externe</option>
              <option value="custom">Autre correction</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Details de la correction..." className={inputCls} data-testid="correction-notes" />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-700 rounded-lg text-sm text-slate-400 hover:bg-slate-800">Annuler</button>
          <button onClick={handleSubmit} disabled={saving || !correctionType}
            className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-lg text-sm disabled:opacity-50"
            data-testid="correction-submit">
            {saving ? 'Enregistrement...' : 'Enregistrer la correction'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================
// ATELIER INVOICES SECTION (in admin atelier page)
// ============================
function AtelierInvoicesSection({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [invRes, cliRes, dashRes] = await Promise.all([
        axios.get(`${API_URL}/api/atelier-billing/admin/invoices`, { headers }),
        axios.get(`${API_URL}/api/atelier-billing/admin/clients`, { headers }),
        axios.get(`${API_URL}/api/atelier-billing/admin/dashboard`, { headers }),
      ]);
      setInvoices(invRes.data);
      setClients(cliRes.data);
      setDashboard(dashRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) fetchData(); }, [fetchData, token]);

  const filteredInvoices = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);
  const pendingCount = invoices.filter(i => i.status === 'pending_approval').length;

  const handleApprove = async (invoiceId) => {
    if (!window.confirm('Approuver cette facture?')) return;
    try {
      await axios.post(`${API_URL}/api/atelier-billing/admin/invoices/${invoiceId}/approve`, {}, { headers });
      toast.success('Facture approuvee');
      fetchData();
      setSelectedInvoice(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleReject = async (invoiceId) => {
    const reason = window.prompt('Raison du refus (optionnel):');
    if (reason === null) return;
    try {
      await axios.post(`${API_URL}/api/atelier-billing/admin/invoices/${invoiceId}/reject`, { reason }, { headers });
      toast.success('Facture retournee au mecanicien');
      fetchData();
      setSelectedInvoice(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSaveInvoice = async (invoice) => {
    try {
      const res = await axios.put(`${API_URL}/api/atelier-billing/admin/invoices/${invoice.id}`, {
        client_id: invoice.client_id,
        client_name: invoice.client_name,
        client_address: invoice.client_address,
        client_email: invoice.client_email,
        rate_type: invoice.rate_type,
        line_items: invoice.line_items,
        taxes_enabled: invoice.taxes_enabled,
        applied_taxes: invoice.applied_taxes,
        notes: invoice.notes,
        custom_invoice_number: invoice.custom_invoice_number,
        status: invoice.status,
      }, { headers });
      toast.success('Facture sauvegardee');
      setSelectedInvoice(res.data);
      setEditMode(false);
      fetchData();
    } catch (e) { toast.error('Erreur de sauvegarde'); }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Supprimer cette facture?')) return;
    try {
      await axios.delete(`${API_URL}/api/atelier-billing/admin/invoices/${invoiceId}`, { headers });
      toast.success('Facture supprimee');
      setSelectedInvoice(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateInvoice = async (description, clientId, workOrderId) => {
    try {
      const res = await axios.post(`${API_URL}/api/atelier-billing/admin/invoices/generate`, {
        client_id: clientId || null,
        description: description || 'Service',
        work_order_id: workOrderId || null,
      }, { headers });
      toast.success('Facture creee');
      setShowCreate(false);
      setSelectedInvoice(res.data);
      setEditMode(true);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/atelier-billing/admin/invoices/${invoiceId}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) { toast.error('Erreur PDF'); }
  };

  const STATUS_MAP = {
    draft: { label: 'Brouillon', cls: 'bg-slate-700 text-slate-300' },
    pending_approval: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
    approved: { label: 'Approuvee', cls: 'bg-green-500/20 text-green-400' },
    sent: { label: 'Envoyee', cls: 'bg-blue-500/20 text-blue-400' },
    paid: { label: 'Payee', cls: 'bg-green-500/20 text-green-400' },
  };

  if (loading) return null;

  const monthly = dashboard?.monthly || [];
  const maxMonthly = Math.max(...monthly.map(m => m.total), 1);
  const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <>
      <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="admin-atelier-invoices">
        {/* Dashboard Stats */}
        {dashboard && (
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-white flex items-center gap-2">
                <DollarSign size={18} className="text-amber-500" /> Facturation Atelier
                {pendingCount > 0 && (
                  <span className="bg-amber-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                    data-testid="pending-count">{pendingCount}</span>
                )}
              </h2>
              <button onClick={() => setShowCreate(true)}
                className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-black px-3 py-1.5 rounded-lg font-medium"
                data-testid="admin-new-atelier-invoice">
                <Plus size={12} /> Nouvelle facture
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Brouillons', value: dashboard.total_draft, count: dashboard.by_status?.draft, border: 'border-slate-700' },
                { label: 'En attente', value: dashboard.total_pending, count: dashboard.by_status?.pending_approval, border: 'border-amber-500/30' },
                { label: 'Envoyees', value: dashboard.total_sent, count: dashboard.by_status?.sent, border: 'border-blue-500/30' },
                { label: 'Payees', value: dashboard.total_paid, count: dashboard.by_status?.paid, border: 'border-emerald-500/30' },
              ].map(s => (
                <div key={s.label} className={`bg-slate-800/50 border ${s.border} rounded-xl p-3`} data-testid={`stat-${s.label.toLowerCase()}`}>
                  <p className="text-xs text-slate-400">{s.label} ({s.count || 0})</p>
                  <p className="text-lg font-bold text-white">{(s.value || 0).toFixed(2)} $</p>
                </div>
              ))}
            </div>

            {/* Mini Monthly Chart */}
            {monthly.length > 0 && (
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-3">
                <p className="text-xs text-slate-400 mb-2">Revenus mensuels</p>
                <div className="flex items-end gap-1 h-20">
                  {monthly.map(m => {
                    const pct = (m.total / maxMonthly) * 100;
                    const paidPct = m.total > 0 ? (m.paid / m.total) * 100 : 0;
                    const monthNum = parseInt(m.month.split('-')[1]) - 1;
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`month-${m.month}`}>
                        <div className="w-full relative rounded-t" style={{ height: `${Math.max(pct, 4)}%` }}>
                          <div className="absolute inset-0 bg-amber-500/20 rounded-t" />
                          <div className="absolute bottom-0 left-0 right-0 bg-amber-500 rounded-t transition-all"
                            style={{ height: `${paidPct}%` }} />
                        </div>
                        <span className="text-[9px] text-slate-500">{MONTH_NAMES[monthNum]}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                    <span className="w-2 h-2 rounded-sm bg-amber-500/20 border border-amber-500/40" /> Total
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-slate-500">
                    <span className="w-2 h-2 rounded-sm bg-amber-500" /> Paye
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status filters + invoice list */}
        <div className="p-4">
          <div className="flex gap-1 mb-3 flex-wrap">
            {[
              { key: 'all', label: `Toutes (${invoices.length})` },
              { key: 'pending_approval', label: `En attente (${pendingCount})` },
              { key: 'draft', label: 'Brouillons' },
              { key: 'approved', label: 'Approuvees' },
              { key: 'paid', label: 'Payees' },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`text-[11px] px-2 py-1 rounded-lg transition-colors ${filter === f.key ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
                {f.label}
              </button>
            ))}
          </div>
          {filteredInvoices.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">Aucune facture{filter !== 'all' ? ' avec ce filtre' : ''}</p>
          )}
          <div className="divide-y divide-slate-800/50 max-h-96 overflow-y-auto">
            {filteredInvoices.map(inv => {
              const st = STATUS_MAP[inv.status] || STATUS_MAP.draft;
              return (
                <div key={inv.id}
                  className="p-3 hover:bg-slate-800/30 cursor-pointer transition-colors flex items-center justify-between"
                  onClick={() => { setSelectedInvoice(inv); setEditMode(false); }}
                  data-testid={`admin-invoice-${inv.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm">{inv.custom_invoice_number || inv.invoice_number}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-xs text-slate-400 truncate">
                      {inv.client_name || 'Sans client'} — {inv.mechanic_name || 'Mecanicien'} — {inv.date}
                    </p>
                  </div>
                  <span className="text-white font-bold text-sm ml-2">{inv.total?.toFixed(2)} $</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Create invoice modal */}
      {showCreate && (
        <CreateInvoiceModal
          clients={clients}
          token={token}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreateInvoice}
        />
      )}

      {/* Invoice detail/edit modal */}
      {selectedInvoice && (
        <InvoiceModal
          invoice={selectedInvoice}
          setInvoice={setSelectedInvoice}
          clients={clients}
          editMode={editMode}
          onClose={() => { setSelectedInvoice(null); setEditMode(false); }}
          onEdit={() => setEditMode(true)}
          onSave={handleSaveInvoice}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDeleteInvoice}
          onDownloadPdf={downloadPdf}
          STATUS_MAP={STATUS_MAP}
        />
      )}
    </>
  );
}


function CreateInvoiceModal({ clients, token, onClose, onCreate }) {
  const [mode, setMode] = useState('work_order');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/atelier-billing/admin/available-work-orders`, { headers })
      .then(r => setWorkOrders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-full max-w-lg max-h-[85vh] overflow-y-auto space-y-4">
        <h3 className="text-white font-bold">Nouvelle facture atelier</h3>

        {/* Mode selector */}
        <div className="flex gap-2">
          <button onClick={() => setMode('work_order')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'work_order' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}
            data-testid="admin-mode-work-order">
            Depuis un bon de travail
          </button>
          <button onClick={() => setMode('manual')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'manual' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}
            data-testid="admin-mode-manual">
            Creation manuelle
          </button>
        </div>

        {/* Work order selection */}
        {mode === 'work_order' && (
          <div className="space-y-2">
            <p className="text-xs text-slate-400">Bons de travail completes disponibles</p>
            {loading && <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500 mx-auto"></div>}
            {!loading && workOrders.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">Aucun bon de travail disponible</p>
            )}
            <div className="max-h-48 overflow-y-auto space-y-1">
              {workOrders.map(wo => (
                <div key={wo.id} onClick={() => setSelectedWO(wo)}
                  className={`bg-slate-800 border rounded-lg p-2.5 cursor-pointer transition-colors ${selectedWO?.id === wo.id ? 'border-amber-500' : 'border-slate-700 hover:border-slate-600'}`}
                  data-testid={`admin-wo-${wo.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium text-sm">{wo.order_number || wo.id.slice(0,8)}</p>
                      <p className="text-xs text-slate-400">
                        {wo.vehicle_brand} {wo.vehicle_model} {wo.vehicle_year}
                        {wo.vehicle_plate ? ` (${wo.vehicle_plate})` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">{(wo.tasks || []).length} tache(s)</span>
                  </div>
                  {wo.client_name && <p className="text-xs text-slate-500 mt-1">Client: {wo.client_name}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual description */}
        {mode === 'manual' && (
          <div>
            <label className="text-xs text-slate-400 block mb-1">Description du service</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Reparation frein, service routier..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="admin-create-description" />
          </div>
        )}

        {/* Client selection */}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Client (optionnel)</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="admin-create-client-select">
            <option value="">-- Selectionner --</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="flex gap-2">
          <button onClick={() => onCreate(description, clientId, mode === 'work_order' ? selectedWO?.id : null)}
            disabled={mode === 'work_order' && !selectedWO}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-create-submit">
            Creer la facture
          </button>
          <button onClick={onClose}
            className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg text-sm hover:bg-slate-700">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}


function InvoiceModal({ invoice, setInvoice, clients, editMode, onClose, onEdit, onSave, onApprove, onReject, onDelete, onDownloadPdf, STATUS_MAP }) {
  const updateLineItem = (idx, field, value) => {
    const items = [...(invoice.line_items || [])];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      items[idx].amount = Math.round((items[idx].quantity || 0) * (items[idx].unit_price || 0) * 100) / 100;
    }
    setInvoice(prev => ({ ...prev, line_items: items }));
  };

  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev, line_items: [...(prev.line_items || []), { description: '', quantity: 1, unit_price: 0, amount: 0 }],
    }));
  };

  const removeLineItem = (idx) => {
    setInvoice(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      const addrParts = [client.address];
      const cityParts = [client.city, client.province, client.postal_code].filter(Boolean);
      if (cityParts.length) addrParts.push(cityParts.join(', '));
      setInvoice(prev => ({
        ...prev, client_id: clientId, client_name: client.name,
        client_address: addrParts.filter(Boolean).join(', '), client_email: client.email || '',
      }));
    }
  };

  const clientPresets = clients.find(c => c.id === invoice.client_id)?.rate_presets || [];
  const applyPreset = (preset) => {
    const items = [...(invoice.line_items || [])];
    items.forEach(item => {
      item.unit_price = preset.value || 0;
      item.amount = Math.round((item.quantity || 0) * item.unit_price * 100) / 100;
    });
    setInvoice(prev => ({ ...prev, rate_type: preset.type, rate_value: preset.value, line_items: items }));
    toast.success(`Prereglage "${preset.name}" applique`);
  };

  const subtotal = (invoice.line_items || []).reduce((s, i) => s + (i.amount || 0), 0);
  const discountPct = Number(invoice.discount_percent) || 0;
  const discount = Math.round(subtotal * discountPct) / 100;
  const taxable = subtotal - discount;
  const st = STATUS_MAP[invoice.status] || STATUS_MAP.draft;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <input type="text" value={invoice.custom_invoice_number || invoice.invoice_number || ''}
                  onChange={e => setInvoice(prev => ({ ...prev, custom_invoice_number: e.target.value }))}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white font-bold text-lg w-40"
                  data-testid="admin-edit-invoice-number" />
              ) : (
                <h3 className="text-white font-bold text-lg">{invoice.custom_invoice_number || invoice.invoice_number}</h3>
              )}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
            </div>
            <p className="text-xs text-slate-400">{invoice.mechanic_name || 'Mecanicien'} — {invoice.date}</p>
          </div>
          <div className="flex items-center gap-2">
            {!editMode && (
              <button onClick={onEdit} className="text-xs bg-slate-800 text-amber-400 px-3 py-1.5 rounded-lg hover:bg-slate-700"
                data-testid="admin-edit-btn">Modifier</button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        {/* Client selector (edit mode) */}
        {editMode && (
          <div>
            <label className="text-xs text-slate-400 block mb-1">Client</label>
            <select value={invoice.client_id || ''} onChange={e => handleClientChange(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="admin-edit-client">
              <option value="">-- Selectionner --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {/* Client info (view mode) */}
        {!editMode && (
          <div className="bg-slate-800/50 rounded-lg p-3 text-sm space-y-1">
            <p className="text-white font-medium">{invoice.client_name || 'Sans client'}</p>
            {invoice.client_address && <p className="text-slate-400 text-xs">{invoice.client_address}</p>}
            {invoice.vehicle_info && <p className="text-slate-400 text-xs">Vehicule: {invoice.vehicle_info}</p>}
            {invoice.work_order_number && <p className="text-xs text-amber-400">BT: {invoice.work_order_number}</p>}
          </div>
        )}

        {/* Rate presets (edit mode) */}
        {editMode && clientPresets.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-amber-400 font-semibold">PREREGLAGES</p>
            <div className="flex flex-wrap gap-1">
              {clientPresets.map((preset, idx) => (
                <button key={idx} onClick={() => applyPreset(preset)}
                  className="px-2 py-1 rounded text-xs font-medium bg-slate-800 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                  data-testid={`admin-preset-${idx}`}>
                  {preset.name} — {preset.value}$
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Line items */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <p className="text-xs text-slate-400 font-semibold">LIGNES DE FACTURATION</p>
            {editMode && (
              <button onClick={addLineItem} className="text-xs text-amber-400 hover:text-amber-300"
                data-testid="admin-add-line">+ Ajouter ligne</button>
            )}
          </div>
          {(invoice.line_items || []).map((item, idx) => (
            <div key={idx} className="bg-slate-800/30 rounded-lg p-2">
              {editMode ? (
                <div className="space-y-1">
                  <div className="flex gap-2 items-center">
                    <input type="text" value={item.description} placeholder="Description"
                      onChange={e => updateLineItem(idx, 'description', e.target.value)}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                      data-testid={`admin-line-desc-${idx}`} />
                    <button onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-500">Qte</label>
                      <input type="number" step="0.01" value={item.quantity}
                        onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white text-center"
                        data-testid={`admin-line-qty-${idx}`} />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-500">Prix unit.</label>
                      <input type="number" step="0.01" value={item.unit_price}
                        onChange={e => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white text-center"
                        data-testid={`admin-line-price-${idx}`} />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-500">Montant</label>
                      <div className="bg-slate-700/50 rounded px-2 py-1 text-sm text-amber-400 text-center font-medium">
                        {item.amount?.toFixed(2)} $
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-white truncate flex-1">{item.description}</span>
                  <span className="text-slate-300 ml-2">{item.quantity} x {item.unit_price?.toFixed(2)}$</span>
                  <span className="text-amber-400 ml-2 font-medium w-20 text-right">{item.amount?.toFixed(2)}$</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="bg-slate-800/50 rounded-lg p-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Sous-total</span>
            <span className="text-white">{subtotal.toFixed(2)} $</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm" data-testid="admin-invoice-discount">
              <span className="text-emerald-400">Réduction fidélité ({discountPct} %)</span>
              <span className="text-emerald-400">-{discount.toFixed(2)} $</span>
            </div>
          )}
          {invoice.taxes_enabled && (invoice.applied_taxes || []).map((tax, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-slate-400">{tax.name} ({tax.rate}%)</span>
              <span className="text-white">{(taxable * tax.rate / 100).toFixed(2)} $</span>
            </div>
          ))}
          <div className="flex justify-between text-base font-bold border-t border-slate-700 pt-1">
            <span className="text-white">Total</span>
            <span className="text-amber-400">
              {(taxable + (invoice.taxes_enabled ? (invoice.applied_taxes || []).reduce((s, t) => s + taxable * t.rate / 100, 0) : 0)).toFixed(2)} $
            </span>
          </div>
        </div>

        {/* Notes */}
        {editMode ? (
          <div>
            <label className="text-xs text-slate-400 block mb-1">Notes</label>
            <textarea value={invoice.notes || ''} onChange={e => setInvoice(prev => ({...prev, notes: e.target.value}))}
              rows={2} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white resize-none"
              data-testid="admin-invoice-notes" />
          </div>
        ) : (
          invoice.notes && <div className="text-xs text-slate-400 italic">Notes: {invoice.notes}</div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 border-t border-slate-800 pt-3">
          <button onClick={() => onDownloadPdf(invoice.id)}
            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm px-3 py-2 bg-blue-500/10 rounded-lg"
            data-testid="admin-download-pdf">
            <Download size={14} /> PDF
          </button>

          {editMode && (
            <button onClick={() => onSave(invoice)}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 rounded-lg text-sm"
              data-testid="admin-save-invoice">
              Sauvegarder
            </button>
          )}

          {invoice.status === 'pending_approval' && (
            <>
              <button onClick={() => onApprove(invoice.id)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg text-sm"
                data-testid="admin-approve-btn">
                Approuver
              </button>
              <button onClick={() => onReject(invoice.id)}
                className="bg-red-600/20 hover:bg-red-600/30 text-red-400 font-semibold py-2 px-4 rounded-lg text-sm border border-red-500/30"
                data-testid="admin-reject-btn">
                Retourner
              </button>
            </>
          )}

          {(invoice.status === 'draft' || invoice.status === 'approved') && (
            <button onClick={() => onDelete(invoice.id)}
              className="text-red-400 hover:text-red-300 text-sm px-3 py-2 bg-red-500/10 rounded-lg"
              data-testid="admin-delete-invoice">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}